import { EntityConf, Locator } from 'src/domains/explorer/types';
import { ListConf } from 'src/domains/query/types';
import { SystemConf } from 'src/domains/system/types';
import { ApplicationConf } from 'src/domains/extension/app/types';
import * as schemaJson from './schema.json';
import * as uiconfJson from './ui.conf.json';
import * as globals from './globals.json';
import _, { Dictionary } from 'lodash';
import { getPrincipal } from 'src/auth';

// Schema ------------------------------------------------------------------------
export interface Schema {
    resources: { [key: string]: Resource }
    relationships: Relationship[]
}

export enum FieldType {
    STRING = 'STRING',
    TEXT = 'TEXT',
    DATE = 'DATE',
    DATE_TIME = 'DATE_TIME',
    INT32 = 'INT32',
    INT64 = 'INT64',
    FLOAT = 'FLOAT',
    DOUBLE = 'DOUBLE',
    BOOLEAN = 'BOOLEAN',
    PASSWORD = 'PASSWORD',
}

export enum RelSide {
    SOURCE = 'OWNER',
    TARGET = 'BACKREF',
}

export enum RelType {
    MANY_TO_ONE = 'MANY_TO_ONE',
    MANY_TO_MANY = 'MANY_TO_MANY',
    ONE_TO_ONE = 'ONE_TO_ONE',
}

export enum PresetSelector {
    RESOURCES = "RESOURCES",
}

export interface Resource {
    name: string
    label?: string
    description?: string
    fields: { [key: string]: Field }
    isprivate?: boolean
    extends?: string
}

export interface Relationship {
    resource: string
    referenceName: string
    referenceLabel?: string
    referenceDescription?: string
    related: string
    backReferenceName: string
    backReferenceLabel?: string
    backReferenceDescription?: string
    metaType: RelType
    contextual: boolean
    hidden: boolean
    bidirectional: boolean
}

export interface Field {
    type: FieldType;
    name: string;
    label?: string
    description?: string
    collection?: boolean;
    hidden?: boolean;
    values?: string[];
    valuesSelector?: PresetSelector;
    readOnly?: boolean;
    writeOnly?: boolean;
    version?: boolean;
    required?: boolean;
}

export interface RelPoint {
    rel: Relationship;
    side: RelSide;
}

export interface RelLink {
    rel: Relationship;
    resource: Locator;
    related: Locator;
}

export type Attribute = Field | RelPoint;

// UI config ------------------------------------------------------------------------
export interface UIConfig {
    routes: { [key: string]: RouteConf };
}

export enum CompType { List, Edit, Create }

export interface BaseComp {
    label?: string
}

export interface ResourceComp {
    resourceName: string
}

export function isResourceComp(comp: any): comp is ResourceComp {
    return comp.resourceName !== undefined;
}

export type ComponentConf = (ListConf | EntityConf | SystemConf | ApplicationConf) & BaseComp;

export interface RouteConf {
    comp: ComponentConf | ComponentConf[]
}

class Configuration {
    schema: Schema;
    uiconf: UIConfig;

    private editableFieldsPredicate: (field: Field) => boolean = (field) => !field.readOnly && !field.collection && !systemFields.includes(field.name);
    private allFieldsPredicate: (field: Field) => boolean = (field) => !field.collection;

    constructor(schemaJson: any, uiJson: any) {
        this.schema = schemaJson as Schema
        this.uiconf = uiJson as UIConfig
    }

    getOutbound(resourceName: string, relType?: RelType): Relationship[] {
        let rels: Relationship[] = this.getSchema().relationships.filter(rel => (rel.resource === resourceName) && (relType ? rel.metaType === relType : true));
        const parent = this.getResource(resourceName).extends;
        if (parent !== undefined) {
            rels = _.concat(this.getOutbound(parent, relType), rels);
        }
        return rels
    }

    getInbound(resourceName: string, relType?: RelType): Relationship[] {
        let rels: Relationship[] = this.getSchema().relationships.filter(rel => (rel.related === resourceName) && (relType ? rel.metaType === relType : true));
        const parent = this.getResource(resourceName).extends;
        if (parent !== undefined) {
            rels = _.concat(this.getInbound(parent, relType), rels);
        }
        return rels
    }

    getRels(name: string, relType: RelType, relDir: RelSide) {
        return relDir === RelSide.SOURCE ? conf.getOutbound(name, relType) : conf.getInbound(name, relType);
    }

    getRelNames(name: string, relType: RelType, relDir: RelSide) {
        return this.getRels(name, relType, relDir).map(rel => relDir === RelSide.SOURCE ? rel.referenceName : rel.backReferenceName);
    }

    getRelVector(resourceName: string, propName: string): RelPoint | undefined {
        const outBound: Relationship[] = this.getSchema().relationships.filter(rel =>
            rel.resource === resourceName && rel.referenceName === propName
        );
        const inBound: Relationship[] = this.getSchema().relationships.filter(rel =>
            rel.related === resourceName && rel.backReferenceName === propName
        );
        if (outBound.length > 0) {
            return { rel: outBound[0], side: RelSide.SOURCE };
        }
        if (inBound.length > 0) {
            return { rel: inBound[0], side: RelSide.TARGET };
        }
        const parent = this.getResource(resourceName).extends;
        if (parent !== undefined) {
            return this.getRelVector(parent, propName);
        }
        return undefined;
    }

    public getSchema(): Schema {
        // return _.cloneDeep((this.schema as any).default);
        return (this.schema as any).default;
    }

    public getResource(resource: string): Resource {
        return conf.getSchema().resources[resource];
    }

    public getResourceNames(): string[] {
        return _.keys(conf.getSchema().resources);
    }

    public getEditableFields(resource: string): Dictionary<Field> {
        const resourceConf: Resource = conf.getSchema().resources[resource];

        const superRes: Resource | null =
            resourceConf.extends !== undefined ?
                conf.getSchema().resources[resourceConf.extends]
                :
                null;

        const editableFields: Dictionary<Field> = _.pickBy(
            superRes !== null ?
                _.merge(resourceConf.fields, superRes.fields)
                :
                resourceConf.fields,

            this.editableFieldsPredicate
        );

        return editableFields;
    }

    public editableFieldsNames(resource: string): string[] {
        return _.keys(this.getEditableFields(resource));
    }

    public getFields(resource: string): Dictionary<Field> {
        const resourceConf: Resource = this.getSchema().resources[resource];
        const fields = _.pickBy(resourceConf.fields, this.allFieldsPredicate);
        const parent = this.getResource(resource).extends;
        if (parent !== undefined) {
            _.assign(fields, this.getFields(parent));
        }
        return fields;
    }

    public getField(resource: string, fieldName: string): Field | undefined {
        return this.getFields(resource)[fieldName];
    }

    public isAbstract(resource: string): boolean {
        const morphs = _.pickBy(this.getSchema().resources, res => res.extends === resource);
        return _.keys(morphs).length > 0;
    }

    public getChildMorphs(resource: string): string[] {
        return _.keys(_.pickBy(this.getSchema().resources, res => res.extends === resource));
    }

    public getSiblingMorphs(resource: string): string[] {
        const resConf = this.getResource(resource);
        return resConf.extends !== undefined ? _.keys(_.pickBy(this.getSchema().resources, res => res.extends === resConf.extends)) : [];
    }

    public isMorph(resource: string): boolean {
        return this.getSchema().resources[resource].extends !== undefined;
    }

    public getSuperName(resource: string): string | undefined {
        return this.getSchema().resources[resource].extends;
    }

    public getUiConf(): UIConfig {
        return (this.uiconf as any).default
    }

    public getAdminRoutes(): { [key: string]: RouteConf } {
        const all = this.getUiConf().routes;
        return _.pick(all, _.keys(all).filter(key => key.startsWith('/admin/')));
    }

    public getUiRoutes(): { [key: string]: RouteConf } {
        const all = this.getUiConf().routes;
        return _.pick(all, _.keys(all).filter(key => key.startsWith('/ui/')));
    }

    public getAttrNames(resource: string, includeSystem?: boolean): string[] {
        let fields = this.getFields(resource);
        let rels = this.getOutbound(resource);
        let invs = this.getInbound(resource);
        if (!includeSystem) {
            fields = _.pickBy(fields, fld => !fld.hidden);
            rels = rels.filter(rel => !rel.hidden);
            invs = invs.filter(rel => !rel.hidden);
        }
        const fieldNames = _.keys(fields).filter(name => !systemFields.includes(name));
        const relsNames = rels.map(rel => rel.referenceName);
        const invsNames = invs.map(inv => inv.backReferenceName);
        return [
            ...fieldNames,
            ...relsNames,
            ...invsNames
        ];
    }

    public getVectors(resource: string): RelPoint[] {
        const rels = this.getOutbound(resource);
        const outs: RelPoint[] = rels.map(rel => ({ rel, side: RelSide.SOURCE }));
        const invs = this.getInbound(resource);
        const ins: RelPoint[] = invs.map(rel => ({ rel, side: RelSide.TARGET }));
        return [
            ...outs,
            ...ins
        ]
    }

    public getContextualVectors(resource: string): RelPoint[] {
        return this.getVectors(resource).filter(relPoint => relPoint.rel.contextual);
    }

    public getAttribute(resource: string, attr: string): Attribute | undefined {
        const field = this.getField(resource, attr);
        if (field) {
            return field;
        }

        const vector = this.getRelVector(resource, attr);
        if (vector) {
            return vector;
        }
    }
}

export const conf = new Configuration(schemaJson, uiconfJson);


const getInstructions: (name: string) => {
    rels: Relationship[],
    side: RelSide
}[] = (name) => [
    { rels: conf.getOutbound(name, RelType.MANY_TO_ONE), side: RelSide.SOURCE },
    { rels: conf.getOutbound(name, RelType.ONE_TO_ONE), side: RelSide.SOURCE },
    { rels: conf.getInbound(name, RelType.ONE_TO_ONE), side: RelSide.TARGET },
];

/**
 * Find all available related resources as a dictionary: { [attrName]: Resource }
*/
export const getAvailAssoc: (resourceName: string, hidden: boolean) => Dictionary<Resource> = (resourceName, hidden) => {
    return getInstructions(resourceName).reduce((acc, instruction) => {
        const { rels, side } = instruction;

        rels.filter(rel => !rel.hidden || hidden).reduce((innerAcc, rel) => {
            const attrName = (rel[side == RelSide.SOURCE ? 'referenceName' : 'backReferenceName']);
            innerAcc[attrName] = conf.getResource(rel[side == RelSide.SOURCE ? 'related' : 'resource']);
            return innerAcc;
        }, acc);

        return acc;
    }, {} as Dictionary<Resource>);
}

interface GlobalPropsObject {
    app: {
        title: string
        debug: boolean
    }
}

class GlobalProps {
    globals: GlobalPropsObject;

    constructor(globalsJson: any) {
        this.globals = globalsJson.default as GlobalPropsObject;
    }

    public getTitle(): string {
        return this.globals.app.title;
    }

    public isDebug(): boolean {
        return this.globals.app.debug;
    }
}

export const globalProps = new GlobalProps(globals);

export const systemFields: string[] = ['_id', '_dateCreated', '_dateUpdated', '_version', '_authorId', '_locked'];
export const systemFieldsAdminWhitelist: string[] = ['_id', '_dateCreated', '_dateUpdated', '_authorId'];
export const systemFieldsUserWhiteList: string[] = [];

export const systemResources: string[] = ['_user', '_role', '_httprequest', '_grid', '_resource', '_field', '_relationship'];
export const systemResourcesAdminWhitelist: string[] = ['_user', '_httprequest'];
export const systemResourcesUserWhiteList: string[] = [];

export function isField(
    attr: any
): attr is Field {
    return (attr as Field).name !== undefined && (attr as Field).type !== undefined;
}

export function isRelationshipVector(
    attr: any
): attr is RelPoint {
    return (attr as RelPoint).rel !== undefined && (attr as RelPoint).side !== undefined;
}

export const getFieldLabel: (field: Field) => string = (field) => field.label || field.name;

export const getResourceLabel: (resource: Resource) => string = (resource) => resource.label || resource.name;
